import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Button, Grid, Stepper, Step, StepLabel, Divider, Alert, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import GrainIcon from '@mui/icons-material/Grain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PanToolIcon from '@mui/icons-material/PanTool';
import HealingIcon from '@mui/icons-material/Healing';
import ScienceIcon from '@mui/icons-material/Science';
import BiotechIcon from '@mui/icons-material/Biotech';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDashboard } from '../../../contexts/DashboardContext';
// Importa o JSON com as imagens e descrições
// Caminho relativo até a raiz do frontend onde o arquivo info-mastitis.json está
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import infoMastitis from '../../../../info-mastitis.json';

interface MastitisTestStep2Props {
  initialData?: any;
}

type CategoryKey = 'ImagemCromogenicoStaphylococcus' | 'ImagemCromogenicoGeral' | 'ImagemCromogenicoStreptococcus';

// Ordem ajustada: Geral vem por último; cores trocadas entre Geral e Streptococcus
const CATEGORY_FLOW: { key: CategoryKey; title: string; hint: string }[] = [
  { key: 'ImagemCromogenicoStaphylococcus', title: 'Cromogênico Staphylococcus (vermelho)', hint: 'Selecione a imagem que mais se aproxima do seu resultado para Staphylococcus.' },
  { key: 'ImagemCromogenicoStreptococcus', title: 'Cromogênico Streptococcus (branco)', hint: 'Selecione a imagem mais compatível com Streptococcus.' },
  { key: 'ImagemCromogenicoGeral', title: 'Cromogênico Geral (transparente)', hint: 'Selecione a imagem correspondente ao resultado geral observado.' },
];

const MastitisTestStep2: React.FC<MastitisTestStep2Props> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedPrimary, setSelectedPrimary] = useState<{ [K in CategoryKey]?: string }>({});
  const [selectedSecondary, setSelectedSecondary] = useState<{ [K in CategoryKey]?: string }>({});
  // Pré-perguntas: Leite (single) e Úbere (multi)
  const [prePhase, setPrePhase] = useState(true);
  const [preStepIndex, setPreStepIndex] = useState(0); // 0: Leite, 1: Úbere
  const [milkCondition, setMilkCondition] = useState<string | null>(null);
  const [udderConditions, setUdderConditions] = useState<string[]>([]);

  const results = (infoMastitis as any)?.ResultsMastitis || [];

  const currentCategory = CATEGORY_FLOW[stepIndex];

  const imagesForCurrent = useMemo(() => {
    const set = new Set<string>();
    for (const item of results) {
      const name = item[currentCategory.key];
      if (name && typeof name === 'string') {
        set.add(name);
      }
    }
    return Array.from(set);
  }, [results, currentCategory.key]);

  const handleSelect = (filename: string) => {
    const key = currentCategory.key;
    const currentPrimary = selectedPrimary[key];
    const currentSecondary = selectedSecondary[key];

    // Sem primário: define primário
    if (!currentPrimary) {
      setSelectedPrimary((prev) => ({ ...prev, [key]: filename }));
      return;
    }

    // Clique novamente na mesma imagem: desfaz primário
    if (currentPrimary === filename) {
      setSelectedPrimary((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest as any;
      });
      return;
    }

    // Com primário definido e clicou numa imagem diferente
    if (!currentSecondary) {
      if (filename !== currentPrimary) {
        setSelectedSecondary((prev) => ({ ...prev, [key]: filename }));
      }
      return;
    }

    // Clique novamente na imagem secundária: desfaz secundário
    if (currentSecondary === filename) {
      setSelectedSecondary((prev) => {
        const { [key]: __, ...rest } = prev;
        return rest as any;
      });
      return;
    }

    // Substitui secundário por novo filename (diferente do primário)
    if (filename !== currentPrimary) {
      setSelectedSecondary((prev) => ({ ...prev, [key]: filename }));
    }
  };

  const computeSeverity = (milk: string | null, udder: string[]): 'grave' | 'medio' | 'leve' => {
    const u = new Set(udder);
    // Grave: sinais de sangue no leite ou feridas no úbere
    if (milk === 'com sangue' || u.has('com ferida')) return 'grave';
    // Moderado: grumos no leite ou dois+ sinais importantes no úbere
    const redFlags = ['inchado', 'quente', 'endurecido', 'dolorido'];
    const countRed = redFlags.filter((k) => u.has(k)).length;
    if (milk === 'com grumos' || countRed >= 2) return 'medio';
    // Leve: ausência de sinais graves/moderados ou poucos sinais no úbere
    return 'leve';
  };

  const handleNext = () => {
    if (prePhase) {
      if (preStepIndex === 0) {
        setPreStepIndex(1);
      } else {
        setPrePhase(false);
        setStepIndex(0);
      }
      return;
    }
    if (stepIndex < CATEGORY_FLOW.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      const severity = computeSeverity(milkCondition, udderConditions);
      const payload = {
        ...(initialData || {}),
        mastitis: {
          ...(initialData?.mastitis || {}),
          plateImageFile: initialData?.mastitis?.plateImageFile,
          collectionDate: initialData?.mastitis?.collectionDate,
          selectionsPrimary: {
            staphylococcus: selectedPrimary['ImagemCromogenicoStaphylococcus'] || null,
            geral: selectedPrimary['ImagemCromogenicoGeral'] || null,
            streptococcus: selectedPrimary['ImagemCromogenicoStreptococcus'] || null,
          },
          selectionsSecondary: {
            staphylococcus: selectedSecondary['ImagemCromogenicoStaphylococcus'] || null,
            geral: selectedSecondary['ImagemCromogenicoGeral'] || null,
            streptococcus: selectedSecondary['ImagemCromogenicoStreptococcus'] || null,
          },
          clinical: {
            milkCondition,
            udderConditions,
            severity,
          },
        },
      };
      updateContent('mastitisTestStep3', payload);
    }
  };

  const handleCancel = () => {
    updateContent('welcome');
  };
  const isPrimarySelectedForCurrent = Boolean(selectedPrimary[currentCategory.key]);

  // Análise parcial progressiva: escolhe o "mais próximo" até a etapa atual
  const keysUpTo = (idx: number): CategoryKey[] => CATEGORY_FLOW.slice(0, idx + 1).map((c) => c.key);
  const matchForUpTo = (sel?: { [K in CategoryKey]?: string } | null, untilIndex = stepIndex) => {
    if (!sel) return null;
    const keys = keysUpTo(untilIndex);
    const activeKeys = keys.filter((k) => Boolean(sel[k]));
    if (activeKeys.length === 0) return null;

    let best: any = null;
    let bestScore = -1;
    for (const item of results) {
      let score = 0;
      for (const k of activeKeys) {
        if (item?.[k] === sel[k]) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    return best;
  };

  const partialPrimary = useMemo(() => matchForUpTo(selectedPrimary, stepIndex), [selectedPrimary, stepIndex, results]);
  const partialSecondary = useMemo(() => matchForUpTo(selectedSecondary, stepIndex), [selectedSecondary, stepIndex, results]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Stepper horizontal indicando progresso */}
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            {prePhase ? (
              <Stepper activeStep={preStepIndex} alternativeLabel>
                <Step key="pre-leite"><StepLabel>Leite</StepLabel></Step>
                <Step key="pre-ubere"><StepLabel>Úbere</StepLabel></Step>
              </Stepper>
            ) : (
              <Stepper activeStep={stepIndex} alternativeLabel>
                {CATEGORY_FLOW.map((cat) => (
                  <Step key={cat.key}>
                    <StepLabel>{cat.title}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>
          <Box>
            <Button variant="text" size="small" onClick={handleCancel} sx={{ color: 'text.secondary', textTransform: 'none' }}>
              Cancelar
            </Button>
          </Box>
        </Box>
      </Paper>

      {prePhase ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, height: '100%' }}>
          {/* Esquerda: cards de seleção */}
          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {preStepIndex === 0 ? 'Como está o leite?' : 'Como está a úbere?'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {preStepIndex === 0 ? 'Selecione uma opção' : 'Selecione uma ou mais opções'}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {preStepIndex === 0 ? (
                [
                  { key: 'normal', label: 'Normal', icon: <LocalDrinkIcon /> },
                  { key: 'com grumos', label: 'Com grumos', icon: <GrainIcon /> },
                  { key: 'aguado', label: 'Aguado', icon: <WaterDropIcon /> },
                  { key: 'com sangue', label: 'Com sangue', icon: <BloodtypeIcon /> },
                ].map((opt) => (
                  <Grid size={{ xs: 6, md: 3 }} key={opt.key}>
                    <Paper
                      onClick={() => setMilkCondition(opt.key)}
                      elevation={1}
                      sx={(theme) => ({
                        p: 2,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        backgroundColor: milkCondition === opt.key ? theme.palette.action.selected : theme.palette.background.paper,
                      })}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{opt.icon}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                    </Paper>
                  </Grid>
                ))
              ) : (
                [
                  { key: 'normal', label: 'Normal', icon: <CheckCircleOutlineIcon /> },
                  { key: 'inchado', label: 'Inchado', icon: <ExpandCircleDownIcon /> },
                  { key: 'quente', label: 'Quente', icon: <WhatshotIcon /> },
                  { key: 'endurecido', label: 'Endurecido', icon: <AcUnitIcon /> },
                  { key: 'dolorido', label: 'Dolorido', icon: <PanToolIcon /> },
                  { key: 'com ferida', label: 'Com ferida', icon: <HealingIcon /> },
                ].map((opt) => {
                  const selected = udderConditions.includes(opt.key);
                  return (
                    <Grid size={{ xs: 6, md: 4 }} key={opt.key}>
                      <Paper
                        onClick={() => {
                          if (opt.key === 'normal') {
                            setUdderConditions(['normal']);
                          } else {
                            setUdderConditions((prev) => {
                              const hasNormal = prev.includes('normal');
                              const base = hasNormal ? prev.filter((x) => x !== 'normal') : prev;
                              if (base.includes(opt.key)) return base.filter((x) => x !== opt.key);
                              return [...base, opt.key];
                            });
                          }
                        }}
                        elevation={1}
                        sx={(theme) => ({
                          p: 2,
                          textAlign: 'center',
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                          backgroundColor: selected ? theme.palette.action.selected : theme.palette.background.paper,
                        })}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{opt.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                      </Paper>
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Paper>

          {/* Direita: resumo e navegação */}
          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <ScienceIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Resumo</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Leite: {milkCondition || '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Úbere: {udderConditions.length > 0 ? udderConditions.join(', ') : '—'}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 2, pb: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (preStepIndex === 1) setPreStepIndex(0);
                }}
                sx={{ textTransform: 'none' }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ textTransform: 'none' }}
                disabled={preStepIndex === 0 ? !milkCondition : udderConditions.length === 0}
              >
                {preStepIndex === 0 ? 'Avançar' : 'Continuar para seleção de imagens'}
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            height: '100%',
          }}
        >
          {/* Coluna Esquerda: instruções e imagens para seleção */}
          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {currentCategory.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {currentCategory.hint}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Selecione até duas imagens por categoria: a primeira define a análise primária e a segunda (opcional) a secundária.
              </Typography>
            </Box>

            {/* Imagens (com badges 1/2) */}
            <Grid container spacing={2} justifyContent="center" sx={{ flex: 1, overflow: 'auto', justifyContent: 'center' }}>
              {imagesForCurrent.map((filename) => {
                const isPrimary = selectedPrimary[currentCategory.key] === filename;
                const isSecondary = selectedSecondary[currentCategory.key] === filename;
                return (
                  <Grid size={{ xs: 6, md: 4 }} key={filename}>
                    <Box
                      onClick={() => handleSelect(filename)}
                      sx={(theme) => ({
                        border: '2px solid',
                        borderColor: isPrimary
                          ? theme.palette.primary.main
                          : isSecondary
                            ? theme.palette.info.main
                            : 'divider',
                        borderRadius: 2,
                        p: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 140,
                        '&:hover': { borderColor: theme.palette.primary.light },
                        position: 'relative',
                      })}
                    >
                      {(isPrimary || isSecondary) && (
                        <Chip
                          label={isPrimary ? '1' : '2'}
                          size="small"
                          color={isPrimary ? 'primary' : 'info'}
                          sx={{ position: 'absolute', top: 6, left: 6, height: 20 }}
                        />
                      )}
                      <Box
                        component="img"
                        src={`/mastitis-imgs/${filename}`}
                        alt={filename}
                        sx={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 1 }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            {/* Controles de navegação movidos para a coluna direita */}
          </Paper>

          {/* Coluna Direita: resultados parciais com destaque e acordeão */}
          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <ScienceIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Resultados</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Os resultados abaixo atualizam automaticamente conforme suas seleções nesta etapa.
            </Typography>

            {/* Destaque para análise primária */}
            {partialPrimary ? (
              <Box sx={(theme) => ({
                p: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
                textAlign: 'center',
                border: `1px solid ${theme.palette.divider}`,
              })}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Análise parcial primária (etapa {stepIndex + 1})</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <ScienceIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{partialPrimary.Microrganismo}</Typography>
                </Box>
              </Box>
            ) : (
              <Alert severity="warning">
                Selecione uma imagem nesta etapa para ver a análise parcial primária.
              </Alert>
            )}

            {/* Acordeão para análise secundária */}
            <Accordion disabled={!selectedSecondary[currentCategory.key]}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BiotechIcon color={selectedSecondary[currentCategory.key] ? 'info' : 'disabled'} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Análise parcial secundária</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {partialSecondary ? (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    Microrganismo sugerido (etapa {stepIndex + 1}): <strong>{partialSecondary.Microrganismo}</strong>
                  </Alert>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Selecione uma segunda imagem (opcional) nesta etapa para sugerir análise secundária.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
            {/* Controles de navegação (à direita, abaixo do diagnóstico secundário) */}
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 2, pb: 1 }}>
              <Button
                variant="outlined"
                disabled={stepIndex === 0}
                onClick={() => {
                  if (stepIndex > 0) setStepIndex((i) => Math.max(0, i - 1));
                  else {
                    setPrePhase(true);
                    setPreStepIndex(1);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                disabled={!isPrimarySelectedForCurrent}
                onClick={handleNext}
                sx={(theme) => ({
                  color: '#fff',
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    color: '#fff',
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                })}
              >
                {stepIndex < CATEGORY_FLOW.length - 1 ? 'Próximo' : 'Concluir'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MastitisTestStep2;
